import {
    Alert,
    Box,
    Paper,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAppSelector } from "../../app/hooks";
import MessageBubble from "./MessageBubble";
import MessageComposer from "./MessageComposer";
import {
    fetchConversationThread,
    openContextConversation,
    sendConversationMessage,
} from "../../lib/api/messages";

export default function ContextChatPanel({
    contextType,
    contextId,
    title,
    enabled = true,
    minHeight = 420,
}: {
    contextType: "client" | "order" | "campaign";
    contextId: string;
    title: string;
    enabled?: boolean;
    minHeight?: number;
}) {
    const queryClient = useQueryClient();
    const currentUserId = useAppSelector((state) => state.auth.user?.id ?? null);

    const contextQuery = useQuery({
        queryKey: ["messages-context", contextType, contextId],
        queryFn: () => openContextConversation(contextType, contextId, title),
        enabled: enabled && contextId !== "",
        staleTime: Number.POSITIVE_INFINITY,
    });

    const conversationId = contextQuery.data?.conversationId ?? null;

    const threadQuery = useQuery({
        queryKey: ["messages-thread", conversationId],
        queryFn: () => fetchConversationThread(conversationId!),
        enabled: conversationId !== null,
        refetchInterval: 15_000,
    });

    const sendMutation = useMutation({
        mutationFn: ({ id, body, files }: { id: number; body: string; files: File[] }) =>
            sendConversationMessage(id, body, files),
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["messages-thread", conversationId] }),
                queryClient.invalidateQueries({ queryKey: ["messages-inbox"] }),
                queryClient.invalidateQueries({ queryKey: ["messages-unread-count"] }),
            ]);
        },
    });

    const messages = threadQuery.data?.messages ?? [];
    const loadError = contextQuery.isError || threadQuery.isError;
    const isLoading = contextQuery.isPending || (conversationId !== null && threadQuery.isPending && messages.length === 0);

    return (
        <Paper className="table-card" elevation={0} sx={{ p: 2, display: "flex", flexDirection: "column", minHeight }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                Екипен чат
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Споделена комуникация за този клиент. Спомени колега с @ и CRM идентификатор (напр. @12).
            </Typography>

            {loadError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Не успяхме да заредим екипния чат.
                </Alert>
            ) : null}

            <Box sx={{ flex: 1, overflowY: "auto", display: "grid", gap: 1, mb: 2, pr: 0.5 }}>
                {isLoading ? <Typography color="text.secondary">Зареждане...</Typography> : null}
                {!isLoading && messages.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                        Все още няма съобщения. Започни разговора с екипа.
                    </Typography>
                ) : null}
                {messages.map((message) => (
                    <MessageBubble key={message.id} message={message} currentUserId={currentUserId} />
                ))}
            </Box>

            {sendMutation.isError ? (
                <Alert severity="error" sx={{ mb: 1 }}>
                    Не успяхме да изпратим съобщението.
                </Alert>
            ) : null}

            <MessageComposer
                disabled={!conversationId || loadError}
                sending={sendMutation.isPending}
                placeholder="Напиши съобщение до екипа..."
                onSend={async (body, files) => {
                    if (!conversationId) {
                        return;
                    }

                    await sendMutation.mutateAsync({ id: conversationId, body, files });
                }}
            />
        </Paper>
    );
}
